import React, { useEffect, useState, useRef } from 'react';
import { importsApi, storesApi } from '../lib/api';
import { Can } from '../lib/usePermissions';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Upload, Check, AlertTriangle, FileSpreadsheet, RefreshCw, ArrowRight, Trash2, Puzzle, Download } from 'lucide-react';
import DailyImportTab from '../components/DailyImportTab';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

// Banner che invita a scaricare la Chrome Extension che automatizza l'import
// giornaliero. Mostra un mini-disclaimer con i 2 caveat onesti (Chrome aperto +
// dipendenza dal DOM Fresha) così l'utente ha aspettative realistiche. Il
// download passa via endpoint OPS-only `/api/imports/extension/download`.
function ChromeExtensionBanner() {
  const [downloading, setDownloading] = useState(false);
  const onDownload = async () => {
    setDownloading(true);
    try {
      const r = await importsApi.downloadExtension();
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'storeops-chrome-extension.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download fallito: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="bg-slate-900 text-white p-5 mb-6 flex items-start gap-4" data-testid="chrome-ext-banner">
      <Puzzle className="w-6 h-6 mt-0.5 flex-shrink-0 text-slate-300" strokeWidth={1.6} />
      <div className="flex-1 min-w-0">
        <div className="font-heading text-base font-semibold mb-1">
          Salta il drag &amp; drop: usa l'estensione Chrome
        </div>
        <p className="text-sm text-slate-300 leading-snug">
          L'estensione "Send to StoreOps" scarica i Riepiloghi del Rendimento
          da Fresha e li manda qui automaticamente. Schedulata alle 09:00 + catch-up
          dei giorni mancanti quando apri Chrome.
        </p>
        <p className="text-xs text-slate-400 mt-2">
          Richiede Chrome aperto perché l'alarm scatti. Funziona finché Fresha non
          cambia il layout della pagina report.
        </p>
      </div>
      <Button
        onClick={onDownload}
        disabled={downloading}
        className="bg-white text-slate-900 hover:bg-slate-100 rounded-none border-0 flex-shrink-0"
        data-testid="btn-download-extension"
      >
        <Download className="w-4 h-4 mr-2" />
        {downloading ? 'Preparo…' : 'Scarica .zip'}
      </Button>
    </div>
  );
}

export default function ImportsPage() {
  const [imports, setImports] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  // Budget import state
  const [budgetYear, setBudgetYear] = useState(2026);
  const [budgetPreview, setBudgetPreview] = useState(null);
  const [uploadingBudget, setUploadingBudget] = useState(false);
  const budgetFileRef = useRef(null);
  // Active tab — daily lands first since it's the new primary flow.
  const [activeTab, setActiveTab] = useState('daily');
  // Costs import state
  const [costsPreview, setCostsPreview] = useState(null);
  const [uploadingCosts, setUploadingCosts] = useState(false);
  const costsFileRef = useRef(null);
  // Auto-create unmapped stores during confirm — opt-out toggle. Default ON
  // so that a fresh import never silently drops a new location: an OPS who
  // wants the legacy behaviour (drop unmatched) can disable it per import.
  const [autoCreateUnmapped, setAutoCreateUnmapped] = useState(true);
  const [autoCreateBudget, setAutoCreateBudget] = useState(true);
  // Last batch of auto-created stores — surfaced inline so OPS can finish
  // setup (brand / area / area_manager) right after confirming.
  const [lastAutoCreated, setLastAutoCreated] = useState([]);

  useEffect(() => {
    Promise.all([importsApi.list(), storesApi.list()])
      .then(([iRes, sRes]) => { setImports(iRes.data); setStores(sRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const refreshImports = async () => {
    const res = await importsApi.list();
    setImports(res.data);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    // No scenario/month/year — auto-detected from filename
    try {
      const res = await importsApi.upload(formData);
      setPreview(res.data);
    } catch (err) {
      alert('Errore upload: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    try {
      const res = await importsApi.confirm(preview.import_id, {
        row_mappings: {},
        auto_create_unmapped: autoCreateUnmapped,
      });
      setLastAutoCreated(res.data?.auto_created_stores || []);
      setPreview(null);
      await refreshImports();
    } catch (err) {
      alert('Errore conferma: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (importId, filename) => {
    if (!window.confirm(`Eliminare l'importazione "${filename}"?\n\nSe confermata, verranno rimossi anche i dati KPI associati.`)) return;
    try {
      await importsApi.delete(importId);
      await refreshImports();
    } catch (err) {
      alert('Errore eliminazione: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleBudgetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBudget(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', budgetYear);
    try {
      const res = await importsApi.uploadBudget(formData);
      setBudgetPreview(res.data);
    } catch (err) {
      alert('Errore upload budget: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingBudget(false);
      if (budgetFileRef.current) budgetFileRef.current.value = '';
    }
  };

  const handleBudgetConfirm = async () => {
    if (!budgetPreview) return;
    try {
      const res = await importsApi.confirmBudget(budgetPreview.import_id, {
        sheet_mappings: {},
        auto_create_unmapped: autoCreateBudget,
      });
      setLastAutoCreated(res.data?.auto_created_stores || []);
      setBudgetPreview(null);
      await refreshImports();
    } catch (err) {
      alert('Errore conferma budget: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCostsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCosts(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await importsApi.uploadCosts(formData);
      setCostsPreview(res.data);
    } catch (err) {
      alert('Errore upload costi: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingCosts(false);
      if (costsFileRef.current) costsFileRef.current.value = '';
    }
  };

  const handleCostsConfirm = async () => {
    if (!costsPreview) return;
    try {
      await importsApi.confirmCosts(costsPreview.import_id, {});
      setCostsPreview(null);
      await refreshImports();
    } catch (err) {
      alert('Errore conferma costi: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="imports-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Import Dati</h1>
        <p className="text-slate-500 mt-1">Importa dati operativi, budget e costi da file Excel</p>
      </div>

      {/* Tab selector */}
      <div className="flex gap-0 mb-6 border-b border-slate-200 overflow-x-auto">
        {[
          { key: 'daily', label: 'Giornaliero' },
          { key: 'fresha', label: 'Mensile (Fresha)' },
          { key: 'budget', label: 'Budget' },
          { key: 'costs', label: 'Costi' },
        ].map(t => (
          <button key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.key ? 'border-black text-black' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            data-testid={`tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {activeTab === 'daily' && (
        <>
          <ChromeExtensionBanner />
          <DailyImportTab onImportComplete={refreshImports} />
        </>
      )}

      {activeTab === 'fresha' && (
        <>
      {lastAutoCreated.length > 0 && (
        <div
          className="border border-emerald-300 bg-emerald-50 p-4 mb-6"
          data-testid="auto-created-stores-banner"
        >
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-900 mb-1">
                {lastAutoCreated.length} {lastAutoCreated.length === 1 ? 'negozio creato' : 'negozi creati'} automaticamente
              </p>
              <p className="text-xs text-emerald-800 mb-2">
                I dati Fresha sono stati salvati e i nuovi negozi sono visibili in stato <strong>"Da rivedere"</strong>.
                Vai alla pagina Negozi per assegnare brand, area e Area Manager.
              </p>
              <ul className="text-sm text-emerald-900 space-y-0.5">
                {lastAutoCreated.map((s) => (
                  <li key={s.store_id} className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-emerald-700/70">{s.fresha_reference}</span>
                    <span aria-hidden="true">→</span>
                    <a
                      href={`/stores/${s.store_id}`}
                      className="font-medium underline decoration-emerald-400 hover:decoration-emerald-700"
                      data-testid={`auto-created-link-${s.store_id}`}
                    >
                      {s.display_name}
                    </a>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setLastAutoCreated([])}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 underline mt-2"
                data-testid="dismiss-auto-created-banner"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section — visibile solo se l'utente ha il permesso */}
      <Can permission="imports.upload">
      <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="upload-section">
        <h2 className="font-heading text-lg font-semibold mb-2">Importa dati Fresha</h2>
        <p className="text-sm text-slate-500 mb-4">
          Il sistema rileva automaticamente anno e mese dal nome del file.
          <br/>
          <span className="text-slate-400">Formati supportati: <span className="font-mono text-xs">Report_2026_M01.xlsx</span>, <span className="font-mono text-xs">Actual_M03.xlsx</span>, <span className="font-mono text-xs">AnnoPrec_M01.xlsx</span></span>
        </p>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">File XLSX</label>
            <Input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={handleUpload}
              className="rounded-none"
              disabled={uploading}
              data-testid="file-input"
            />
          </div>
        </div>
        {uploading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 mt-3">
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            Elaborazione in corso...
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="import-preview">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-lg font-semibold">Anteprima importazione</h2>
              <p className="text-sm text-slate-500">
                {preview.filename} — {preview.row_count} righe trovate
                {' — '}{MONTHS[(preview.month || 1) - 1]} {preview.year}
                {preview.detected_from_filename && (
                  <span className="text-xs text-slate-400 ml-1">(rilevato dal nome file)</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-2 text-xs text-slate-700 select-none cursor-pointer mr-1"
                data-testid="auto-create-actuals-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={autoCreateUnmapped}
                  onChange={(e) => setAutoCreateUnmapped(e.target.checked)}
                  data-testid="auto-create-actuals-toggle"
                  className="accent-slate-900"
                />
                Crea automaticamente i negozi non mappati
              </label>
              <Button
                variant="outline"
                onClick={() => setPreview(null)}
                className="rounded-none"
                data-testid="cancel-import-btn"
              >
                Annulla
              </Button>
              <Button
                onClick={handleConfirm}
                className="bg-black text-white hover:bg-slate-900 rounded-none"
                data-testid="confirm-import-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {preview.has_duplicates ? 'Conferma e sovrascrivi' : 'Conferma importazione'}
              </Button>
            </div>
          </div>

          {/* Duplicate warning banner */}
          {preview.has_duplicates && (
            <div className="flex items-start gap-3 px-4 py-3 mb-4 border border-amber-300 bg-amber-50" data-testid="duplicate-warning">
              <RefreshCw className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {preview.duplicate_count} {preview.duplicate_count === 1 ? 'negozio ha' : 'negozi hanno'} gi&agrave; dati importati per questo periodo
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Confermando, i dati esistenti verranno sovrascritti con i nuovi valori. I dati precedenti restano nello storico importazioni.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {preview.rows?.map(row => (
              <div
                key={row.row_id}
                className={`px-4 py-3 border ${
                  !row.matched ? 'border-amber-200 bg-amber-50' :
                  row.has_existing_data ? 'border-blue-200 bg-blue-50' :
                  'border-emerald-200 bg-emerald-50'
                }`}
                data-testid={`preview-row-${row.row_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {!row.matched ? (
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    ) : row.has_existing_data ? (
                      <RefreshCw className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Check className="w-4 h-4 text-emerald-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{row.store_reference}</p>
                      {row.matched && <p className="text-xs text-emerald-700">&rarr; {row.store_name}</p>}
                      {!row.matched && <p className="text-xs text-amber-700">Negozio non mappato</p>}
                      {row.has_existing_data && (
                        <p className="text-xs text-blue-700 mt-0.5">
                          Sovrascrittura — dati esistenti da: {row.previous_import?.filename || 'import precedente'}
                          {row.previous_import?.confirmed_at && (
                            <span className="text-blue-500"> ({new Date(row.previous_import.confirmed_at).toLocaleDateString('it-IT')})</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <p className="font-mono">{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(row.parsed_data?.total_sales || 0)}</p>
                    <p className="text-xs text-slate-500">{row.parsed_data?.services_sold || 0} servizi</p>
                  </div>
                </div>

                {/* Diff: old vs new values */}
                {row.diff && Object.keys(row.diff).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-blue-500 mb-1">Variazioni rispetto ai dati esistenti</p>
                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                      {Object.entries(row.diff).map(([key, d]) => (
                        <DiffChip key={key} kpiKey={key} oldVal={d.old} newVal={d.new} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-emerald-700">{preview.matched_count - (preview.duplicate_count || 0)} nuovi</span>
            {preview.duplicate_count > 0 && (
              <span className="text-blue-700">{preview.duplicate_count} sovrascritture</span>
            )}
            {preview.unmatched_count > 0 && (
              <span className="text-amber-700">{preview.unmatched_count} non mappati</span>
            )}
          </div>
        </div>
      )}
      </Can>

      {/* Import History */}
      <div className="bg-white border border-slate-200" data-testid="import-history">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-base font-semibold">Storico importazioni Fresha</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : imports.filter(i => i.import_type !== 'budget').length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessuna importazione effettuata</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Scenario</th>
                <th>Periodo</th>
                <th className="text-right">Righe</th>
                <th className="text-right">Mappate</th>
                <th>Stato</th>
                <th>Data</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {imports.filter(i => i.import_type !== 'budget').map(imp => (
                <tr key={imp.import_id}>
                  <td data-label="File" className="font-medium">{imp.filename}</td>
                  <td data-label="Scenario">
                    <span className="text-xs uppercase tracking-wider font-medium px-2 py-0.5 border bg-slate-50">
                      {imp.scenario === 'actual' ? 'Dati' : imp.scenario === 'budget' ? 'Budget' : imp.scenario}
                    </span>
                  </td>
                  <td data-label="Periodo" className="num">{imp.month ? `${String(imp.month).padStart(2, '0')}/` : ''}{imp.year}</td>
                  <td data-label="Righe" className="num">{imp.row_count || imp.sheet_count || 0}</td>
                  <td data-label="Mappate" className="num">{imp.matched_count}</td>
                  <td data-label="Stato">
                    <span className={`text-xs uppercase tracking-wider font-medium px-2 py-0.5 border ${
                      imp.status === 'confirmed' ? 'semaforo-green' : 'semaforo-yellow'
                    }`}>
                      {imp.status === 'confirmed' ? 'Confermato' : 'Anteprima'}
                    </span>
                  </td>
                  <td data-label="Data" className="text-xs text-slate-500">{new Date(imp.created_at).toLocaleString('it-IT')}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(imp.import_id, imp.filename)}
                      className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      title="Elimina importazione"
                      data-testid={`delete-import-${imp.import_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
        </>
      )}

      {/* ======================== BUDGET TAB ======================== */}
      {activeTab === 'budget' && (
        <>
          <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="budget-upload-section">
            <h2 className="font-heading text-lg font-semibold mb-2">Import Budget da file</h2>
            <p className="text-sm text-slate-500 mb-4">
              Il file deve avere un foglio per ogni negozio (nome foglio = nome negozio)
              con colonne: Mese | Totale delle vendite
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Anno</label>
                <Select value={String(budgetYear)} onValueChange={(v) => setBudgetYear(Number(v))}>
                  <SelectTrigger className="rounded-none" data-testid="budget-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">File XLSX</label>
                <Input
                  ref={budgetFileRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleBudgetUpload}
                  className="rounded-none"
                  disabled={uploadingBudget}
                  data-testid="budget-file-input"
                />
              </div>
            </div>
            {uploadingBudget && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-3">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Elaborazione budget in corso...
              </div>
            )}
          </div>

          {/* Budget Preview */}
          {budgetPreview && (
            <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="budget-preview">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold">Anteprima import budget</h2>
                  <p className="text-sm text-slate-500">
                    {budgetPreview.filename} — {budgetPreview.sheet_count} fogli trovati — Anno {budgetPreview.year}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <label
                    className="flex items-center gap-2 text-xs text-slate-700 select-none cursor-pointer mr-1"
                    data-testid="auto-create-budget-toggle-label"
                  >
                    <input
                      type="checkbox"
                      checked={autoCreateBudget}
                      onChange={(e) => setAutoCreateBudget(e.target.checked)}
                      data-testid="auto-create-budget-toggle"
                      className="accent-slate-900"
                    />
                    Crea automaticamente i negozi non mappati
                  </label>
                  <Button variant="outline" onClick={() => setBudgetPreview(null)} className="rounded-none">
                    Annulla
                  </Button>
                  <Button
                    onClick={handleBudgetConfirm}
                    className="bg-black text-white hover:bg-slate-900 rounded-none"
                    data-testid="confirm-budget-import-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {budgetPreview.has_duplicates ? 'Conferma e sovrascrivi' : 'Conferma budget'}
                  </Button>
                </div>
              </div>

              {budgetPreview.has_duplicates && (
                <div className="flex items-start gap-3 px-4 py-3 mb-4 border border-amber-300 bg-amber-50">
                  <RefreshCw className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Alcuni negozi hanno gi&agrave; budget per questo anno. I valori verranno sovrascritti.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {budgetPreview.sheets?.map(sheet => (
                  <div
                    key={sheet.sheet_name}
                    className={`px-4 py-3 border ${
                      !sheet.matched ? 'border-amber-200 bg-amber-50' :
                      sheet.has_existing_data ? 'border-blue-200 bg-blue-50' :
                      'border-emerald-200 bg-emerald-50'
                    }`}
                    data-testid={`budget-sheet-${sheet.sheet_name}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {!sheet.matched ? (
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        ) : sheet.has_existing_data ? (
                          <RefreshCw className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Check className="w-4 h-4 text-emerald-600" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{sheet.sheet_name}</p>
                          {sheet.matched && <p className="text-xs text-emerald-700">&rarr; {sheet.store_name}</p>}
                          {!sheet.matched && <p className="text-xs text-amber-700">Negozio non trovato nel sistema</p>}
                          {sheet.has_existing_data && <p className="text-xs text-blue-600">Budget esistente — verr&agrave; sovrascritto</p>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-3 text-xs font-mono">
                          {Object.entries(sheet.months || {}).sort((a,b) => a[0]-b[0]).map(([m, v]) => (
                            <div key={m} className="text-center">
                              <span className="text-slate-400 block">M{m}</span>
                              <span className="font-medium">{new Intl.NumberFormat('it-IT', {style:'currency', currency:'EUR', maximumFractionDigits:0}).format(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-emerald-700">{budgetPreview.matched_count} mappati</span>
                {budgetPreview.unmatched_count > 0 && (
                  <span className="text-amber-700">{budgetPreview.unmatched_count} non trovati</span>
                )}
              </div>
            </div>
          )}

          {/* Budget import history */}
          <div className="bg-white border border-slate-200" data-testid="budget-import-history">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading text-base font-semibold">Storico import budget</h2>
            </div>
            {imports.filter(i => i.import_type === 'budget').length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nessun budget importato</p>
              </div>
            ) : (
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Anno</th>
                    <th className="text-right">Fogli</th>
                    <th className="text-right">Mappati</th>
                    <th>Stato</th>
                    <th>Data</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {imports.filter(i => i.import_type === 'budget').map(imp => (
                    <tr key={imp.import_id}>
                      <td data-label="File" className="font-medium">{imp.filename}</td>
                      <td data-label="Anno" className="num">{imp.year}</td>
                      <td data-label="Fogli" className="num">{imp.sheet_count}</td>
                      <td data-label="Mappati" className="num">{imp.matched_count}</td>
                      <td data-label="Stato">
                        <span className={`text-xs uppercase tracking-wider font-medium px-2 py-0.5 border ${
                          imp.status === 'confirmed' ? 'semaforo-green' : 'semaforo-yellow'
                        }`}>
                          {imp.status === 'confirmed' ? 'Confermato' : 'Anteprima'}
                        </span>
                      </td>
                      <td data-label="Data" className="text-xs text-slate-500">{new Date(imp.created_at).toLocaleString('it-IT')}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(imp.import_id, imp.filename)}
                          className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ======================== COSTS TAB ======================== */}
      {activeTab === 'costs' && (
        <>
          <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="costs-upload-section">
            <h2 className="font-heading text-lg font-semibold mb-2">Import Costi da file</h2>
            <p className="text-sm text-slate-500 mb-4">
              Il file deve avere un foglio per mese (es. "GENNAIO 2026") con i costi per negozio.
              <br/>
              <span className="text-slate-400">Il sistema rileva automaticamente mese, anno e negozi dal file.</span>
            </p>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">File XLSX</label>
                <Input ref={costsFileRef} type="file" accept=".xlsx" onChange={handleCostsUpload}
                  className="rounded-none" disabled={uploadingCosts} data-testid="costs-file-input" />
              </div>
            </div>
            {uploadingCosts && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-3">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Elaborazione costi in corso...
              </div>
            )}
          </div>

          {/* Costs Preview */}
          {costsPreview && (
            <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="costs-preview">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold">Anteprima import costi</h2>
                  <p className="text-sm text-slate-500">{costsPreview.filename} — {costsPreview.sheet_count} fogli</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => setCostsPreview(null)} className="rounded-none">Annulla</Button>
                  <Button onClick={handleCostsConfirm} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="confirm-costs-import-btn">
                    <Check className="w-4 h-4 mr-2" /> Conferma costi
                  </Button>
                </div>
              </div>
              {costsPreview.sheets?.map(sheet => (
                <div key={sheet.sheet_name} className="mb-4">
                  <h3 className="font-heading text-base font-semibold mb-2">{sheet.sheet_name}</h3>
                  <div className="space-y-1.5">
                    {sheet.stores?.filter(s => s.total > 0).map(s => (
                      <div key={s.file_name}
                        className={`flex items-center justify-between px-4 py-2 border ${
                          s.matched ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
                        }`}>
                        <div className="flex items-center gap-3">
                          {s.matched ? <Check className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                          <div>
                            <span className="text-sm font-medium">{s.file_name}</span>
                            {s.matched && <span className="text-xs text-emerald-700 ml-2">&rarr; {s.store_name}</span>}
                            {!s.matched && <span className="text-xs text-amber-700 ml-2">Non trovato</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-sm font-semibold">{new Intl.NumberFormat('it-IT', {style:'currency', currency:'EUR'}).format(s.total)}</span>
                          <span className="text-xs text-slate-400 ml-2">{s.costs?.length} voci</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{sheet.matched_count} mappati, {sheet.unmatched_count} non trovati</p>
                </div>
              ))}
            </div>
          )}

          {/* Costs import history */}
          <div className="bg-white border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-heading text-base font-semibold">Storico import costi</h2>
            </div>
            {imports.filter(i => i.import_type === 'costs').length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nessun file costi importato</p>
              </div>
            ) : (
              <table className="w-full data-table">
                <thead>
                  <tr><th>File</th><th>Fogli</th><th className="text-right">Costi</th><th>Stato</th><th>Data</th><th></th></tr>
                </thead>
                <tbody>
                  {imports.filter(i => i.import_type === 'costs').map(imp => (
                    <tr key={imp.import_id}>
                      <td data-label="File" className="font-medium">{imp.filename}</td>
                      <td data-label="Fogli" className="num">{imp.sheet_count}</td>
                      <td data-label="Costi" className="num">{imp.total_costs || '-'}</td>
                      <td data-label="Stato">
                        <span className={`text-xs uppercase tracking-wider font-medium px-2 py-0.5 border ${
                          imp.status === 'confirmed' ? 'semaforo-green' : 'semaforo-yellow'
                        }`}>{imp.status === 'confirmed' ? 'Confermato' : 'Anteprima'}</span>
                      </td>
                      <td data-label="Data" className="text-xs text-slate-500">{new Date(imp.created_at).toLocaleString('it-IT')}</td>
                      <td>
                        <button onClick={() => handleDelete(imp.import_id, imp.filename)}
                          className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}


const KPI_LABELS = {
  total_sales: 'Vendite',
  services_count: 'Servizi',
  occupancy_pct: '% Occupazione',
  avg_ticket: 'Ticket medio',
  review_count: 'Recensioni',
  new_client_pct: '% Nuovi clienti',
};

function fmtDiff(val, key) {
  if (val == null) return '-';
  if (key.includes('pct') || key.includes('rate')) return `${val.toFixed(1)}%`;
  if (key.includes('revenue') || key.includes('sales') || key.includes('ticket')) {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  }
  return Math.round(val).toLocaleString('it-IT');
}

function DiffChip({ kpiKey, oldVal, newVal }) {
  const label = KPI_LABELS[kpiKey] || kpiKey;
  return (
    <span className="text-xs text-blue-800 inline-flex items-center gap-1" data-testid={`diff-${kpiKey}`}>
      <span className="font-medium">{label}:</span>
      <span className="line-through text-blue-400">{fmtDiff(oldVal, kpiKey)}</span>
      <ArrowRight className="w-3 h-3 text-blue-500" />
      <span className="font-semibold">{fmtDiff(newVal, kpiKey)}</span>
    </span>
  );
}
